import { z } from 'zod';
import { everyWordMatches } from './search/everyWord.js';
import { WorksiteTypeSchema } from './schemas/worksite.schema.js';
import { boundedDateRange } from './schemas/payroll.schema.js';

/* ===========================================================================
 * THE SITE REGISTER'S NARROWING — one rule, on the server (merge E2).
 *
 * The Worksites list applied its search and its three filters in the browser
 * (`worksiteListUrl.ts`), so a file of "the list as filtered" would have needed
 * a second copy of the rule on the server, to be kept in step by hand. The
 * rule lives here now; `GET /api/worksites` applies it to the rows it answers
 * AND to the file it writes under the same address, and the screen renders
 * what the server sent. One rule, read by both.
 *
 * The params are the filter registry's server params (`filters/registry.ts`):
 * `q` (every word, somewhere, over name, code, city and emirate — what the
 * Overview's Sites board searches), `type` (the registry's `siteType`),
 * `emirate` (as stored, trimmed) and `status` (`active` / `inactive`).
 * ======================================================================== */

export const WORKSITE_LIST_STATUSES = ['active', 'inactive'] as const;
export type WorksiteListStatus = (typeof WORKSITE_LIST_STATUSES)[number];

/** `GET /api/worksites` — the register, narrowed; `format` asks for its file. */
export const WorksiteListQuerySchema = z.object({
  q: z.string().trim().max(80).optional(),
  type: WorksiteTypeSchema.optional(),
  emirate: z.string().trim().max(80).optional(),
  status: z.enum(WORKSITE_LIST_STATUSES).optional(),
  /** The bulk bar's export (owner, 2026-11-02): only these sites, comma-separated. */
  ids: z
    .string()
    .regex(/^[A-Za-z0-9_-]{1,40}(,[A-Za-z0-9_-]{1,40}){0,199}$/)
    .optional(),
  format: z.enum(['json', 'excel', 'csv']).optional(),
});
export type WorksiteListQuery = z.infer<typeof WorksiteListQuerySchema>;

/** What the rule reads off a site. */
export interface WorksiteListFields {
  name: string;
  code: string | null;
  city: string | null;
  emirate: string | null;
  type: string;
  isActive: boolean;
}

/** The words a site is found by — what the search box's placeholder says. */
export function worksiteSearchFields(w: WorksiteListFields): (string | null)[] {
  return [w.name, w.code, w.city, w.emirate];
}

export function worksiteListMatches(w: WorksiteListFields, f: Omit<WorksiteListQuery, 'format'>): boolean {
  if (f.type && w.type !== f.type) return false;
  if (f.status === 'active' && !w.isActive) return false;
  if (f.status === 'inactive' && w.isActive) return false;
  if (f.emirate && w.emirate?.trim() !== f.emirate) return false;
  return everyWordMatches(f.q ?? '', worksiteSearchFields(w));
}

/** The register as the search and the three filters leave it, in the order given. */
export function narrowWorksiteList<T extends WorksiteListFields>(rows: readonly T[], f: Omit<WorksiteListQuery, 'format'>): T[] {
  const only = f.ids ? new Set(f.ids.split(',')) : null;
  return rows.filter((w) => worksiteListMatches(w, f) && (!only || only.has((w as { id?: string }).id ?? '')));
}

/**
 * The filter options, read off the WHOLE register before the cut — so
 * choosing a type never empties its own list: the types the register holds
 * (not the seven-value enum, §3.11) and the emirates on file, blanks dropped.
 */
export function worksiteListFacets(rows: readonly WorksiteListFields[]): { types: string[]; emirates: string[] } {
  return {
    types: [...new Set(rows.map((w) => w.type))].sort(),
    emirates: [...new Set(rows.map((w) => w.emirate?.trim()).filter((e): e is string => !!e))].sort((a, b) =>
      a.localeCompare(b)
    ),
  };
}

/** A company-time calendar day (docs/time-and-dates.md): `YYYY-MM-DD`. */
const CompanyDay = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/);

/** The longest history a file covers: a quarter, one Overview Sites board per day. */
export const WORKSITE_HISTORY_MAX_DAYS = 92;

/**
 * `GET /api/worksites/history` — site × day manpower over company days
 * `from`..`to`, for the sites the register's filters leave. Excel only.
 */
export const WorksiteHistoryQuerySchema = WorksiteListQuerySchema.extend({
  from: CompanyDay,
  to: CompanyDay,
  format: z.enum(['excel']).optional(),
}).superRefine(boundedDateRange(WORKSITE_HISTORY_MAX_DAYS));
export type WorksiteHistoryQuery = z.infer<typeof WorksiteHistoryQuerySchema>;

/** What the Overview's Sites board narrows a row by. */
export interface BoardSiteFields {
  name: string;
  code: string | null;
  city: string | null;
  emirate: string | null;
  type: string | null;
  /** Has coordinates to draw. */
  mappable: boolean;
}

/**
 * THE OVERVIEW SITES BOARD'S NARROWING (merge E3): one kind of site
 * (`siteType`, server `type`), only those not on the map (`noLocation`; a
 * remote "site" has no place to be missing), and the search, every word over
 * the fields the register searches. The board and its file both read it.
 */
export function boardSiteMatches(s: BoardSiteFields, f: { type?: string | null; noLocation?: boolean; q?: string }): boolean {
  if (f.type != null && f.type !== '' && s.type !== f.type) return false;
  if (f.noLocation && (s.mappable || s.type === 'REMOTE')) return false;
  return everyWordMatches(f.q ?? '', [s.name, s.code, s.city, s.emirate]);
}

/** The Camps board's narrowing: over capacity or within it, and every word over name and code. */
export function boardCampMatches(
  c: { name: string; code: string | null; capacity: number | null; residents: number },
  f: { occupancy?: string; q?: string }
): boolean {
  const over = c.capacity !== null && c.residents > c.capacity;
  if (f.occupancy === 'over' && !over) return false;
  if (f.occupancy === 'within' && over) return false;
  return everyWordMatches(f.q ?? '', [c.name, c.code]);
}
